import type { EventInfo } from '@graphql/gql/graphql';
import type { GetServerSidePropsContext } from 'next';

import getConfig from 'next/config';
import { getServerSideApolloClientOptions } from '@/apollo/apolloClient';
import { initApolloClient, ApolloClientFactory } from '@cvent/apollo-client';
import { getDomainRedirect } from '@server/customDomain';
import parseEnvironment from '@server/parseEnvironment';
import isValidUserAgent from '@server/userAgent';
import isFeatureEnabled, { RequiredFeatureSettings } from '@tools/isFeatureEnabled';
import { buildAbsoluteUrl } from '@auth/url';
import resolveLocale from '@server/resolveLocale';
import { ParsedUrlQuery, stringify as encodeQueryString, parse as decodeQueryString } from 'querystring';
import { parseQueryParam } from './parseQueryParam';

const { publicRuntimeConfig } = getConfig();

// The cookie set by automation test so we can distinguish the event/data generated from automation accounts vs real
// accounts.
export const AUTOMATION_TESTS_COOKIE_NAME = 'is-automation-account';
export const SYNTHETICS_COOKIE_NAME = 'is-synthetics-account';
export const GPC_ENABLED_HEADER = 'sec-gpc';
export const USER_AGENT_HEADER = 'user-agent';

// check if request is from an automation account
export const isAutomationAccountRequest = (context: GetServerSidePropsContext) =>
  context.req.cookies?.[AUTOMATION_TESTS_COOKIE_NAME] === 'true' ||
  context.req.cookies?.[SYNTHETICS_COOKIE_NAME] === 'true';

/**
 * Global Privacy Control header needs to be passed down to the client so that do-not-sell requests can be submitted based on its boolean value.
 * "1" indicating user does not want their data shared or sold.
 * "0" if the user consents to their data being shared or sold.
 */
export const isGPCEnabled = (context: GetServerSidePropsContext) => context.req.headers[GPC_ENABLED_HEADER] === '1';

/**
 * Gets the query string for our url we're building.  In particular, we want to pass tracking (e.g. "utm") parameters
 * through to the landing page.
 *
 * We want to ignore some other parameters on the fallback url, like `eventId`.
 */
export const getLandingPageQueryString = (queryString: ParsedUrlQuery, append = false): string => {
  const queryStringObj = decodeQueryString(encodeQueryString(queryString));

  if (queryStringObj.eventId) {
    delete queryStringObj.eventId;
  }
  if (queryStringObj.environment) {
    delete queryStringObj.environment;
  }
  const updatedQueryString = encodeQueryString(queryStringObj);
  if (updatedQueryString !== '') {
    return append ? `&${updatedQueryString}` : `?${updatedQueryString}`;
  }
  return '';
};

/**
 * Provider for common App props based on the current server side context
 */
export const getAppPropProviderForContext = (context: GetServerSidePropsContext, httpLogRequestId: string) => {
  const eventId = parseQueryParam(context.params, 'eventId');
  const environment = parseEnvironment(context);
  const apolloClient = initApolloClient(
    {},
    new ApolloClientFactory({
      initialState: {},
      loginUrl: '',
      graphBasePath: '',
      graphUri: ''
    }),
    getServerSideApolloClientOptions(publicRuntimeConfig.GRAPHQL_URI_EXTERNAL, environment)
  );
  return {
    eventId,
    parsedContextEnvironment: environment,
    isGPCEnabled: isGPCEnabled(context),
    isValidUserAgent: isValidUserAgent(context.req.headers[USER_AGENT_HEADER], eventId, httpLogRequestId),
    isAutomationAccountRequest: isAutomationAccountRequest(context),
    /** Note: this client does not include AppFeaturesDirectiveLink (@appFeature tag) */
    apolloClient,
    getDomainRedirect: (
      eventInfo: Pick<EventInfo, 'customDomain'>,
      appFeatures: Record<string, boolean>,
      skipCustomDomain?: boolean
    ) =>
      getDomainRedirect(
        context.req.headers.host,
        context.req.url,
        eventInfo.customDomain,
        appFeatures,
        skipCustomDomain
      ),
    isPublicLandingPageFeatureEnabled: (eventInfo: { features?: RequiredFeatureSettings[] }) =>
      isFeatureEnabled(eventInfo?.features, 'HAS_PUBLIC_LANDING_PAGE'),
    isWebinarFeatureEnabled: (eventInfo: { features?: RequiredFeatureSettings[] }) =>
      isFeatureEnabled(eventInfo.features, 'WEBINAR_NAVIGATION'),
    buildEventUrl: (path?: string) => {
      const url = path
        ? `${publicRuntimeConfig.BASE_PATH}/events/${eventId}/${path}`
        : `${publicRuntimeConfig.BASE_PATH}/events/${eventId}`;
      return buildAbsoluteUrl(context, url);
    },
    buildErrorHandlingUrl: () => {
      return buildAbsoluteUrl(context, `${publicRuntimeConfig.BASE_PATH}/api/login-error/${eventId}`);
    },
    getInitialLocale: async (eventInfo: Pick<EventInfo, 'defaultLocale'>) => {
      return resolveLocale(context, apolloClient, eventInfo.defaultLocale, eventId, environment);
    }
  };
};
